import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { GlassPanel, FloatingActionButton, Button, Icon } from '../components/common'
import { EntityModal } from '../components/EntityModal'
import { LifetimePrompt } from '../components/Payment'
import { useEvents, useEventOperations, useCharacters } from '../hooks/useFirestore'
import { useProject } from '../context/ProjectContext'
import { useMonetizationState } from '../hooks/useMonetizationState'
import { deleteFiles, extractPublicIdFromUrl } from '../services/storage'
import './TimelineView.css'

/**
 * Era definitions for timeline segmentation
 * Each era gets a visual percentage of the timeline width
 */
const ERAS = [
  { id: 'ancient', label: 'Ancient', start: -2560, end: -500, widthPercent: 15 },
  { id: 'classical', label: 'Classical', start: -500, end: 500, widthPercent: 15 },
  { id: 'medieval', label: 'Medieval', start: 500, end: 1500, widthPercent: 15 },
  { id: 'earlyModern', label: 'Early Modern', start: 1500, end: 1900, widthPercent: 15 },
  { id: 'modern', label: 'Modern', start: 1900, end: 1980, widthPercent: 15 },
  { id: 'contemporary', label: 'Contemporary', start: 1980, end: 2030, widthPercent: 25 },
]

// Calculate cumulative start positions for each era
const ERA_POSITIONS = ERAS.reduce((acc, era, index) => {
  const prevEnd = index > 0 ? acc[index - 1].endPercent : 0
  acc.push({
    ...era,
    startPercent: prevEnd,
    endPercent: prevEnd + era.widthPercent,
  })
  return acc
}, [])

/**
 * Convert a year to a percentage position on the timeline
 */
function yearToPercent(year) {
  for (const era of ERA_POSITIONS) {
    if (year >= era.start && year < era.end) {
      const progress = (year - era.start) / (era.end - era.start)
      return era.startPercent + (progress * era.widthPercent)
    }
  }
  // Default to end if beyond range
  if (year >= 2030) return 100
  if (year < -2560) return 0
  return 50
}

/**
 * Parse a date string and extract the year
 * Supports various formats: "1992", "1992-03-15", "500 BCE", "June 26, 1969", etc.
 */
function parseYear(dateStr) {
  if (!dateStr) return null
  
  const str = String(dateStr).trim()
  
  // Handle BCE dates
  if (str.toLowerCase().includes('bce') || str.toLowerCase().includes('bc')) {
    const match = str.match(/(\d+)/)?.[1]
    return match ? -parseInt(match, 10) : null
  }
  
  // Handle "Month Day, Year" or "Month Day, Year AD" formats
  // Match 4-digit year anywhere in the string (word boundary ensures full year)
  const fourDigitYearMatch = str.match(/\b(\d{4})\b/)
  if (fourDigitYearMatch) {
    return parseInt(fourDigitYearMatch[1], 10)
  }
  
  // Handle short year formats (1-3 digits) at start of string
  const shortYearMatch = str.match(/^-?(\d{1,3})(?!\d)/)
  if (shortYearMatch) {
    return parseInt(shortYearMatch[0], 10)
  }
  
  return null
}

/**
 * Get image URL from various formats
 */
const getImageUrl = (image) => {
  if (!image) return ''
  if (typeof image === 'string') return image
  return image.url || ''
}

/**
 * Collect all image publicIds from an entity
 */
const collectImagePublicIds = (entity) => {
  const publicIds = []
  
  if (entity.primaryImage) {
    if (typeof entity.primaryImage === 'object' && entity.primaryImage.publicId) {
      publicIds.push(entity.primaryImage.publicId)
    } else if (typeof entity.primaryImage === 'string') {
      const extracted = extractPublicIdFromUrl(entity.primaryImage)
      if (extracted) publicIds.push(extracted)
    }
  }
  
  if (entity.extraImages && Array.isArray(entity.extraImages)) {
    for (const img of entity.extraImages) {
      if (typeof img === 'object' && img.publicId) {
        publicIds.push(img.publicId)
      } else if (typeof img === 'string') {
        const extracted = extractPublicIdFromUrl(img)
        if (extracted) publicIds.push(extracted)
      }
    }
  }
  
  return publicIds.filter(Boolean)
}

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20, filter: 'blur(4px)' },
  animate: { 
    opacity: 1, 
    y: 0, 
    filter: 'blur(0px)',
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
  },
  exit: { opacity: 0, y: -20, filter: 'blur(4px)', transition: { duration: 0.2 } }
}

const nodeVariants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: {
      delay: i * 0.05,
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1]
    }
  }),
  hover: {
    scale: 1.15,
    zIndex: 100,
    transition: { duration: 0.2 }
  }
}

const avatarVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 + i * 0.03,
      duration: 0.3
    }
  })
}

/**
 * TimelineView - Interactive timeline with zoom/pan, event nodes, and character avatars
 */
export default function TimelineView() {
  // Container ref for measuring
  const containerRef = useRef(null)
  const timelineRef = useRef(null)
  
  // Zoom and pan state
  const [zoom, setZoom] = useState(1)
  const [panOffset, setPanOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 })
  const [containerWidth, setContainerWidth] = useState(0)
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState('create')
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [originRect, setOriginRect] = useState(null)
  const fabRef = useRef(null)

  // Project context for scoped data access
  const { projectId } = useProject()
  
  // Monetization state for lifetime prompt
  const { 
    shouldShowTimelinePrompt, 
    dismissTimelinePrompt,
    loading: monetizationLoading,
  } = useMonetizationState()
  
  // Firestore data (project-scoped)
  const { data: allEvents, loading: eventsLoading, error: eventsError } = useEvents(projectId, {
    orderByField: 'createdAt',
    orderDirection: 'desc',
  })
  const { data: allCharacters, loading: charsLoading } = useCharacters(projectId)
  const eventOps = useEventOperations(projectId)
  
  // Filter events that should show on timeline
  const timelineEvents = useMemo(() => {
    return allEvents.filter(event => event.showOnTimeline !== false && parseYear(event.date) !== null)
  }, [allEvents])
  
  // Check if timeline has connected events (for lifetime prompt trigger)
  const hasConnectedEvents = useMemo(() => {
    return timelineEvents.some(event => {
      const hasLinks = 
        (event.linkedCharacters?.length > 0) ||
        (event.linkedLocations?.length > 0) ||
        (event.linkedEvents?.length > 0)
      return hasLinks
    })
  }, [timelineEvents])
  
  // Should show lifetime prompt: events exist with connections + not dismissed recently
  const showLifetimePrompt = !monetizationLoading && 
    timelineEvents.length > 0 && 
    hasConnectedEvents && 
    shouldShowTimelinePrompt()
  
  // Build character lookup map
  const charactersById = useMemo(() => {
    const map = {}
    allCharacters.forEach(char => {
      map[char.id] = char
    })
    return map
  }, [allCharacters])
  
  // Calculate node positions with collision avoidance
  const nodePositions = useMemo(() => {
    const MIN_NODE_SIZE = 40
    const MAX_NODE_SIZE = 70
    const NODE_SPACING = 10
    
    // Sort events by date
    const sorted = [...timelineEvents].sort((a, b) => {
      return (parseYear(a.date) || 0) - (parseYear(b.date) || 0)
    })
    
    const positions = []
    const occupied = [] // Track occupied vertical positions at each x
    
    sorted.forEach((event, index) => {
      const year = parseYear(event.date)
      const xPercent = yearToPercent(year)
      
      // Check for collisions with existing nodes
      let yOffset = 0
      let size = MAX_NODE_SIZE
      
      // Find collisions at this x position
      const collisions = positions.filter(pos => {
        const distance = Math.abs(pos.xPercent - xPercent) * 10 // Scale up for easier comparison
        return distance < (MAX_NODE_SIZE + NODE_SPACING) / 100
      })
      
      if (collisions.length > 0) {
        // First try reducing size
        size = Math.max(MIN_NODE_SIZE, MAX_NODE_SIZE - collisions.length * 10)
        // Then stack vertically if needed
        yOffset = collisions.length * (size + NODE_SPACING)
      }
      
      positions.push({
        event,
        xPercent,
        yOffset,
        size,
        year,
        index
      })
    })
    
    return positions
  }, [timelineEvents])
  
  // Measure container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth)
      }
    }
    
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])
  
  // Handle zoom with Shift+scroll
  const handleWheel = useCallback((e) => {
    if (e.shiftKey) {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -0.2 : 0.2
      setZoom(prev => Math.max(1, Math.min(10, prev + delta)))
    }
  }, [])
  
  // Handle pan by dragging
  const handleMouseDown = useCallback((e) => {
    if (zoom > 1 && timelineRef.current) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX,
        scrollLeft: timelineRef.current.scrollLeft
      })
    }
  }, [zoom])
  
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !timelineRef.current) return
    const dx = e.clientX - dragStart.x
    timelineRef.current.scrollLeft = dragStart.scrollLeft - dx
    setPanOffset(timelineRef.current.scrollLeft)
  }, [isDragging, dragStart])
  
  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])
  
  // Fit all: reset zoom and pan
  const handleFitAll = () => {
    setZoom(1)
    setPanOffset(0)
    if (timelineRef.current) {
      timelineRef.current.scrollLeft = 0
    }
  }
  
  // Modal handlers
  const openCreateModal = (event) => {
    setModalMode('create')
    setSelectedEvent(null)
    if (event?.currentTarget) {
      setOriginRect(event.currentTarget.getBoundingClientRect())
    } else if (fabRef.current) {
      setOriginRect(fabRef.current.getBoundingClientRect())
    }
    setModalOpen(true)
  }
  
  const openEditModal = (eventData, clickEvent) => {
    setModalMode('edit')
    setSelectedEvent(eventData)
    if (clickEvent?.currentTarget) {
      setOriginRect(clickEvent.currentTarget.getBoundingClientRect())
    }
    setModalOpen(true)
  }
  
  const handleSave = async (data) => {
    try {
      // Ensure showOnTimeline is set for new events
      const saveData = { ...data, showOnTimeline: data.showOnTimeline ?? true }
      if (modalMode === 'create') {
        await eventOps.add(saveData)
      } else {
        await eventOps.update(selectedEvent.id, saveData)
      }
      setModalOpen(false)
    } catch (err) {
      console.error('Failed to save event:', err)
      throw err
    }
  }
  
  const handleDelete = async () => {
    try {
      const publicIds = collectImagePublicIds(selectedEvent)
      if (publicIds.length > 0) {
        await deleteFiles(publicIds)
      }
      await eventOps.remove(selectedEvent.id)
      setModalOpen(false)
    } catch (err) {
      console.error('Failed to delete event:', err)
      throw err
    }
  }
  
  const loading = eventsLoading || charsLoading
  const error = eventsError
  
  // Calculate timeline width based on zoom
  const timelineWidth = Math.max(100, zoom * 100)
  
  return (
    <motion.div 
      className="timeline-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      ref={containerRef}
    >
      {/* Header */}
      <header className="timeline-view__header">
        <div className="timeline-view__title-group">
          <span className="timeline-view__icon">
            <Icon name="timeline" size={28} />
          </span>
          <h1 className="timeline-view__title">Timeline</h1>
        </div>
        <p className="timeline-view__subtitle">
          {loading ? 'Loading...' : `${timelineEvents.length} event${timelineEvents.length !== 1 ? 's' : ''} on timeline`}
        </p>
      </header>
      
      {/* Error State */}
      {error && (
        <GlassPanel glow="amber" padding="lg" className="timeline-view__error">
          <h3>Error loading events</h3>
          <p>{error.message}</p>
        </GlassPanel>
      )}
      
      {/* Loading State */}
      {loading && !error && (
        <div className="timeline-view__loading">
          <div className="timeline-view__spinner" />
          <p>Loading timeline...</p>
        </div>
      )}
      
      {/* Empty State */}
      {!loading && !error && timelineEvents.length === 0 && (
        <GlassPanel glow="cyan" padding="xl" className="timeline-view__empty">
          <div className="timeline-view__empty-icon"><Icon name="timeline" size={48} /></div>
          <h2>No events on timeline</h2>
          <p>Create events with "Show on Timeline" enabled to see them here.</p>
        </GlassPanel>
      )}
      
      {/* Timeline Canvas */}
      {!loading && !error && timelineEvents.length > 0 && (
        <div 
          className={`timeline-view__canvas-container ${isDragging ? 'is-dragging' : ''} ${zoom > 1 ? 'is-zoomed' : ''}`}
          ref={timelineRef}
          onWheel={handleWheel}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div 
            className="timeline-view__canvas"
            style={{ width: `${timelineWidth}%` }}
          >
            {/* Era Markers */}
            <div className="timeline-view__eras">
              {ERA_POSITIONS.map((era, index) => (
                <div
                  key={era.id}
                  className={`timeline-view__era timeline-view__era--${era.id}`}
                  style={{
                    left: `${era.startPercent}%`,
                    width: `${era.widthPercent}%`
                  }}
                >
                  <span className="timeline-view__era-label">{era.label}</span>
                  <span className="timeline-view__era-range">
                    {era.start < 0 ? `${Math.abs(era.start)} BCE` : era.start} – {era.end < 0 ? `${Math.abs(era.end)} BCE` : era.end}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Timeline Axis */}
            <div className="timeline-view__axis">
              <div className="timeline-view__line" />
            </div>
            
            {/* Event Nodes */}
            <div className="timeline-view__nodes">
              <AnimatePresence>
                {nodePositions.map(({ event, xPercent, yOffset, size, year, index }) => {
                  const imageUrl = getImageUrl(event.primaryImage)
                  const linkedChars = (event.linkedCharacters || [])
                    .map(id => charactersById[id])
                    .filter(Boolean)
                  
                  return (
                    <motion.div
                      key={event.id}
                      className="timeline-view__node-wrapper"
                      style={{
                        left: `${xPercent}%`,
                        bottom: `${60 + yOffset}px`
                      }}
                      custom={index}
                      variants={nodeVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover="hover"
                      onClick={(e) => openEditModal(event, e)}
                    >
                      <div 
                        className="timeline-view__node"
                        style={{ 
                          width: size, 
                          height: size,
                          '--node-size': `${size}px`
                        }}
                      >
                        {imageUrl ? (
                          <img 
                            src={imageUrl} 
                            alt={event.title}
                            className="timeline-view__node-image"
                          />
                        ) : (
                          <span className="timeline-view__node-icon"><Icon name="node-circle" size={24} /></span>
                        )}
                        <div className="timeline-view__node-glow" />
                      </div>
                      
                      {/* Node Connector Line */}
                      <div 
                        className="timeline-view__node-connector"
                        style={{ height: `${60 + yOffset - 40}px` }}
                      />
                      
                      {/* Event Title Tooltip */}
                      <div className="timeline-view__node-tooltip">
                        <div className="timeline-view__node-title">{event.title}</div>
                        <div className="timeline-view__node-year">
                          {year < 0 ? `${Math.abs(year)} BCE` : year}
                        </div>
                      </div>
                      
                      {/* Character Avatars */}
                      {linkedChars.length > 0 && (
                        <div className="timeline-view__avatars">
                          {linkedChars.slice(0, 3).map((char, i) => {
                            const charImage = getImageUrl(char.primaryImage)
                            return (
                              <motion.div
                                key={char.id}
                                className="timeline-view__avatar"
                                custom={i}
                                variants={avatarVariants}
                                initial="hidden"
                                animate="visible"
                                title={char.name}
                              >
                                {charImage ? (
                                  <img src={charImage} alt={char.name} />
                                ) : (
                                  <Icon name="characters" size={16} />
                                )}
                              </motion.div>
                            )
                          })}
                          {linkedChars.length > 3 && (
                            <div className="timeline-view__avatar timeline-view__avatar--more">
                              +{linkedChars.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  )
                })}
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}
      
      {/* Controls - Below Timeline */}
      {!loading && !error && timelineEvents.length > 0 && (
        <div className="timeline-view__controls">
          <GlassPanel padding="sm" className="timeline-view__control-panel">
            <div className="timeline-view__zoom-control">
              <span className="timeline-view__zoom-label">Zoom</span>
              <input
                type="range"
                min="1"
                max="10"
                step="0.1"
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="timeline-view__zoom-slider"
              />
              <span className="timeline-view__zoom-value">{zoom.toFixed(1)}x</span>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleFitAll}
              leftIcon="⊞"
            >
              Fit All
            </Button>
          </GlassPanel>
          <p className="timeline-view__hint">
            <kbd>Shift</kbd> + scroll to zoom • Drag to pan when zoomed
          </p>
        </div>
      )}
      
      {/* Lifetime Prompt - Shows when timeline has connected events */}
      <LifetimePrompt
        variant="timeline"
        isVisible={showLifetimePrompt}
        onDismiss={dismissTimelinePrompt}
      />
      
      {/* Floating Action Button */}
      <div ref={fabRef}>
        <FloatingActionButton
          icon={<Icon name="plus" size={24} />}
          variant="primary"
          size="lg"
          position="bottom-right"
          label="Add new event"
          onClick={openCreateModal}
        />
      </div>
      
      {/* Entity Modal */}
      <EntityModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        entityType="event"
        mode={modalMode}
        initialData={selectedEvent}
        onSave={handleSave}
        onDelete={modalMode === 'edit' ? handleDelete : undefined}
        originRect={originRect}
      />
    </motion.div>
  )
}

